<script setup>
import { computed, getCurrentInstance, ref } from 'vue'
import { DriverStore } from '../pinia/Driver.js'
import emitter from '../utils/emitter'

const MAX_PRESTIGE = 5
const LEVEL_REQUIRED = 100

const Driver = DriverStore()
const { proxy } = getCurrentInstance()
const loading = ref(false)
const error = ref('')

const prestige = computed(() => Number(Driver.persona?.prestige) || 0)
const level = computed(() => Number(Driver.persona?.level) || 0)
const isMax = computed(() => prestige.value >= MAX_PRESTIGE)
const isReady = computed(() => !isMax.value && level.value >= LEVEL_REQUIRED)
const levelsLeft = computed(() => Math.max(0, LEVEL_REQUIRED - level.value))
const progress = computed(() => isMax.value ? 100 : Math.min(100, Math.round(level.value / LEVEL_REQUIRED * 100)))
const ladder = computed(() => Array.from({ length: MAX_PRESTIGE }, (_, index) => {
    const n = index + 1
    return { n, done: prestige.value >= n, next: !isMax.value && prestige.value + 1 === n }
}))
const statusEyebrow = computed(() => isMax.value ? 'Complete' : isReady.value ? 'Ready' : 'In progress')
const statusTitle = computed(() => isMax.value ? 'Maximum prestige reached' : isReady.value ? 'Prestige up available' : 'Reach level 100 to prestige')
const statusCopy = computed(() => {
    if (isMax.value) return 'You hold Prestige 5 — the highest driver rank on NightRiderz.'
    if (isReady.value) return 'Prestiging raises your rank by 1 and resets this driver to level 1.'
    const left = levelsLeft.value
    return `${left} level${left === 1 ? '' : 's'} left before you can advance to Prestige ${prestige.value + 1}.`
})
const actionLabel = computed(() => isMax.value ? 'Maximum reached' : isReady.value ? `Prestige to ${prestige.value + 1}` : `Level ${level.value} / ${LEVEL_REQUIRED}`)

async function prestigeUp() {
    if (!isReady.value || loading.value) return
    error.value = ''
    loading.value = true
    try {
        const result = (await proxy.$http.post('', JSON.stringify({
            serviceName: 'account',
            methodName: 'PrestigeUp',
            parameters: [],
        })))?.data
        if (result === true || result === 1 || result === '1' || result === 'true') {
            const next = Math.min(MAX_PRESTIGE, prestige.value + 1)
            Driver.persona.prestige = next
            Driver.persona.level = 1
            if (Driver.persona.rep != null) Driver.persona.rep = 1
            if (Driver.persona.repAtCurrentLevel != null) Driver.persona.repAtCurrentLevel = 1
            emitter.emit('shell-notify', {
                type: 'success',
                title: 'Prestige up',
                message: `You're now Prestige ${next}. Level reset to 1.`,
            })
            return
        }
        if (typeof result === 'string' && result.length) {
            error.value = result
            return
        }
        if (result?.error) {
            error.value = result.error
            return
        }
        error.value = 'Could not upgrade prestige'
    } catch (err) {
        error.value = err.response?.data?.error || 'Could not upgrade prestige'
    } finally {
        loading.value = false
    }
}
</script>

<template><div class="settings-tab-page prestige-page">
    <div class="settings-tab-header">
        <div class="header-icon">
            <v-icon size="28">mdi-shield-star</v-icon>
        </div>
        <div class="header-content">
            <h3 class="page-title">Prestige</h3>
            <p class="page-subtitle">Reset at level 100 to climb the prestige ladder</p>
        </div>
    </div>

    <v-alert class="error mb-3" v-if="error" :text="error" type="error"></v-alert>

    <div class="section-list">
        <div class="section-row">
            <div class="section-body prestige-hero">
                <div class="prestige-hero-top">
                    <div class="prestige-badge" :class="{ max: isMax, ready: isReady }">
                        <v-icon size="22">{{ isMax ? 'mdi-trophy' : 'mdi-crown' }}</v-icon>
                        <div class="prestige-badge-copy">
                            <span class="prestige-badge-label">Current prestige</span>
                            <strong class="prestige-badge-value">{{ prestige }} <span>/ {{ MAX_PRESTIGE }}</span></strong>
                        </div>
                    </div>
                    <div class="prestige-status">
                        <span class="prestige-status-eyebrow" :class="{ ready: isReady, max: isMax }">{{ statusEyebrow }}</span>
                        <strong>{{ statusTitle }}</strong>
                        <p>{{ statusCopy }}</p>
                    </div>
                </div>
                <div class="prestige-ladder" aria-label="Prestige ladder">
                    <div v-for="(step, index) in ladder" :key="step.n" class="ladder-item">
                        <div class="ladder-node" :class="{ done: step.done, next: step.next, locked: !step.done && !step.next }">
                            <v-icon v-if="step.done" size="16">mdi-check</v-icon>
                            <span v-else>{{ step.n }}</span>
                        </div>
                        <span class="ladder-label">P{{ step.n }}</span>
                        <div v-if="index < ladder.length - 1" class="ladder-bridge" :class="{ filled: step.done }"></div>
                    </div>
                </div>
            </div>
        </div>

        <div class="section-row">
            <div class="section-body prestige-progress-card">
                <div class="progress-head">
                    <div>
                        <div class="setting-label">
                            <v-icon size="18" class="mr-2">mdi-speedometer</v-icon>
                            Level progress
                        </div>
                        <p class="setting-description">
                            {{ isMax ? 'Level no longer needed for prestige.' : `Prestige unlocks at level ${LEVEL_REQUIRED}.` }}
                        </p>
                    </div>
                    <div class="progress-level">
                        <strong>{{ isMax ? LEVEL_REQUIRED : level }}</strong>
                        <span>/ {{ LEVEL_REQUIRED }}</span>
                    </div>
                </div>
                <div class="progress-track" role="progressbar" :aria-valuenow="progress" aria-valuemin="0" aria-valuemax="100">
                    <div class="progress-fill" :class="{ ready: isReady, max: isMax }" :style="{ width: progress + '%' }"></div>
                </div>
                <div class="progress-meta">
                    <span>{{ progress }}%</span>
                    <span v-if="!isMax && !isReady">{{ levelsLeft }} to go</span>
                    <span v-else-if="isReady">Ready now</span>
                    <span v-else>Complete</span>
                </div>
            </div>
        </div>

        <div class="section-row">
            <div class="section-body prestige-notes">
                <div class="note-item">
                    <v-icon size="18">mdi-refresh</v-icon>
                    <div>
                        <strong>Level resets</strong>
                        <p>Your driver returns to level 1 after each prestige.</p>
                    </div>
                </div>
                <div class="note-item">
                    <v-icon size="18">mdi-shield-star</v-icon>
                    <div>
                        <strong>Rank stays</strong>
                        <p>Your prestige number is permanent and shows on your profile.</p>
                    </div>
                </div>
                <div class="note-item">
                    <v-icon size="18">mdi-flag-checkered</v-icon>
                    <div>
                        <strong>Five ranks</strong>
                        <p>Climb from Prestige 1 to Prestige 5 — then you're done.</p>
                    </div>
                </div>
            </div>
        </div>

        <div class="section-row">
            <div class="section-body prestige-action">
                <v-btn v-if="isReady" class="prestige-btn" color="primary" size="large" block :loading="loading" :disabled="loading" @click="prestigeUp">
                    <v-icon class="mr-2">mdi-arrow-up-bold-circle</v-icon>
                    {{ actionLabel }}
                </v-btn>
                <div v-else class="prestige-action-idle" :class="{ max: isMax }">
                    <v-icon size="20">{{ isMax ? 'mdi-trophy-variant' : 'mdi-lock' }}</v-icon>
                    <div>
                        <strong>{{ actionLabel }}</strong>
                        <p v-if="isMax">Nothing left to unlock on this ladder.</p>
                        <p v-else>Keep racing until you hit level {{ LEVEL_REQUIRED }}.</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div></template>

<style scoped>
.error {
  margin-bottom:0}
.prestige-hero {
  display:flex;
  flex-direction:column;
  gap:18px}
.prestige-hero-top {
  display:grid;
  grid-template-columns:minmax(0,150px) minmax(0,1fr);
  gap:14px;
  align-items:stretch}
.prestige-badge {
  display:flex;
  flex-direction:column;
  justify-content:center;
  gap:10px;
  padding:14px;
  border-radius:12px;
  border:1px solid color-mix(in srgb,var(--accent-gold, #ffc107) 28%,var(--line));
  background:color-mix(in srgb,var(--accent-gold, #ffc107) 10%,var(--glass))}
.prestige-badge.ready {
  border-color:color-mix(in srgb,var(--status-success, #66bb6a) 40%,var(--line));
  background:color-mix(in srgb,var(--status-success, #66bb6a) 12%,var(--glass))}
.prestige-badge.max {
  border-color:color-mix(in srgb,var(--accent-gold, #ffc107) 45%,var(--line));
  background:color-mix(in srgb,var(--accent-gold, #ffc107) 16%,var(--glass))}
.prestige-badge .v-icon {
  color:var(--accent-gold, #ffc107)!important}
.prestige-badge.ready .v-icon {
  color:var(--status-success, #66bb6a)!important}
.prestige-badge-label {
  display:block;
  font-size:.68rem;
  font-weight:700;
  letter-spacing:.06em;
  text-transform:uppercase;
  color:var(--mute)}
.prestige-badge-value {
  display:flex;
  align-items:baseline;
  gap:4px;
  font-size:2.4rem;
  font-weight:800;
  line-height:1;
  color:var(--ink)}
.prestige-badge-value span {
  font-size:1rem;
  font-weight:700;
  color:var(--mute)}
.prestige-status {
  display:flex;
  flex-direction:column;
  justify-content:center;
  gap:4px;
  min-width:0}
.prestige-status-eyebrow {
  width:fit-content;
  padding:2px 8px;
  border-radius:999px;
  font-size:.66rem;
  font-weight:800;
  letter-spacing:.05em;
  text-transform:uppercase;
  color:var(--mute);
  background:var(--glass-strong);
  border:1px solid var(--line)}
.prestige-status-eyebrow.ready {
  color:var(--status-success, #66bb6a);
  border-color:color-mix(in srgb,var(--status-success, #66bb6a) 35%,var(--line));
  background:color-mix(in srgb,var(--status-success, #66bb6a) 12%,transparent)}
.prestige-status-eyebrow.max {
  color:var(--accent-gold, #ffc107);
  border-color:color-mix(in srgb,var(--accent-gold, #ffc107) 35%,var(--line));
  background:color-mix(in srgb,var(--accent-gold, #ffc107) 12%,transparent)}
.prestige-status strong {
  font-size:1rem;
  font-weight:800;
  line-height:1.25;
  color:var(--ink)}
.prestige-status p {
  margin:0;
  font-size:.8rem;
  line-height:1.4;
  color:var(--mute)}
.prestige-ladder {
  display:grid;
  grid-template-columns:repeat(5,minmax(0,1fr));
  gap:0;
  align-items:start}
.ladder-item {
  position:relative;
  display:flex;
  flex-direction:column;
  align-items:center;
  gap:6px}
.ladder-node {
  position:relative;
  z-index:1;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:34px;
  height:34px;
  border-radius:50%;
  border:1.5px solid var(--line-strong);
  background:var(--glass-strong);
  color:var(--mute);
  font-size:.78rem;
  font-weight:800}
.ladder-node.done {
  color:#1a1400;
  border-color:color-mix(in srgb,var(--accent-gold, #ffc107) 55%,var(--line));
  background:var(--accent-gold, #ffc107)}
.ladder-node.next {
  color:var(--ink);
  border-color:color-mix(in srgb,var(--status-success, #66bb6a) 55%,var(--line));
  background:color-mix(in srgb,var(--status-success, #66bb6a) 18%,var(--glass-strong));
  box-shadow:0 0 0 3px color-mix(in srgb,var(--status-success, #66bb6a) 16%,transparent)}
.ladder-node.locked {
  opacity:.72}
.ladder-label {
  font-size:.68rem;
  font-weight:700;
  letter-spacing:.04em;
  color:var(--mute)}
.ladder-bridge {
  position:absolute;
  top:16px;
  left:calc(50% + 17px);
  width:calc(100% - 34px);
  height:2px;
  background:var(--line);
  z-index:0}
.ladder-bridge.filled {
  background:color-mix(in srgb,var(--accent-gold, #ffc107) 70%,var(--line))}
.prestige-progress-card {
  display:flex;
  flex-direction:column;
  gap:12px}
.progress-head {
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:12px}
.progress-level {
  display:flex;
  align-items:baseline;
  gap:3px;
  flex-shrink:0;
  font-variant-numeric:tabular-nums}
.progress-level strong {
  font-size:1.35rem;
  font-weight:800;
  line-height:1;
  color:var(--ink)}
.progress-level span {
  font-size:.82rem;
  font-weight:700;
  color:var(--mute)}
.progress-track {
  overflow:hidden;
  height:10px;
  border-radius:999px;
  background:var(--glass-strong);
  border:1px solid var(--line)}
.progress-fill {
  height:100%;
  border-radius:inherit;
  background:linear-gradient(90deg,color-mix(in srgb,var(--accent-gold, #ffc107) 70%,#fff),var(--accent-gold, #ffc107));
  transition:width .35s cubic-bezier(.22,1,.36,1)}
.progress-fill.ready {
  background:linear-gradient(90deg,color-mix(in srgb,var(--status-success, #66bb6a) 70%,#fff),var(--status-success, #66bb6a))}
.progress-fill.max {
  background:linear-gradient(90deg,color-mix(in srgb,var(--accent-gold, #ffc107) 55%,#fff),var(--accent-gold, #ffc107))}
.progress-meta {
  display:flex;
  justify-content:space-between;
  gap:10px;
  font-size:.72rem;
  font-weight:700;
  color:var(--mute)}
.prestige-notes {
  display:flex;
  flex-direction:column;
  gap:12px}
.note-item {
  display:flex;
  align-items:flex-start;
  gap:10px}
.note-item .v-icon {
  margin-top:1px;
  color:var(--accent-gold, #ffc107)!important;
  flex-shrink:0}
.note-item strong {
  display:block;
  font-size:.86rem;
  font-weight:700;
  color:var(--ink)}
.note-item p {
  margin:2px 0 0;
  font-size:.76rem;
  line-height:1.35;
  color:var(--mute)}
.prestige-action {
  padding-top:2px;
  padding-bottom:2px}
.prestige-btn {
  font-weight:800!important;
  letter-spacing:.02em}
.prestige-action-idle {
  display:flex;
  align-items:flex-start;
  gap:12px;
  padding:4px 2px}
.prestige-action-idle .v-icon {
  margin-top:2px;
  color:var(--mute)!important;
  flex-shrink:0}
.prestige-action-idle.max .v-icon {
  color:var(--accent-gold, #ffc107)!important}
.prestige-action-idle strong {
  display:block;
  font-size:.9rem;
  font-weight:800;
  color:var(--ink)}
.prestige-action-idle p {
  margin:2px 0 0;
  font-size:.76rem;
  line-height:1.35;
  color:var(--mute)}
@media (max-width: 520px) {
.prestige-hero-top {
    grid-template-columns:1fr}
.ladder-node {
    width:30px;
    height:30px}
.ladder-bridge {
    top:14px;
    left:calc(50% + 15px);
    width:calc(100% - 30px)}

}
</style>
